import { DotLottie, DotLottieReact as DotLottieReactOriginal } from '@lottiefiles/dotlottie-react'
import { Dialog, DialogContent, IconButton, Paper, PaperProps, Stack } from '@mui/material'
import { FC, ReactNode, useEffect, useState } from 'react'
import { Dialogs, useDialogs } from '../../Contexts/DialogsContext'
import CloseIcon from '../Icons/close'

let DotLottieReact: any
if (typeof window !== 'undefined') {
    try {
        const dynamic = require('next/dynamic')
        DotLottieReact = dynamic(() => import('@lottiefiles/dotlottie-react').then((mod) => mod.DotLottieReact), {
            ssr: false,
        })
    } catch {
        DotLottieReact = DotLottieReactOriginal
    }
} else {
    DotLottieReact = DotLottieReactOriginal
}

type ResponsivePosition = Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', string | number>>

interface AnimatedProps extends PaperProps {
    animationOrImage?: {
        src: string
        loop?: boolean
        autoplay?: boolean
        segment?: [number, number]
        verticalPosition?: ResponsivePosition
        horizontalPosition?: ResponsivePosition
    }
    closeIcon?: {
        hide?: boolean
        verticalPosition?: ResponsivePosition
        horizontalPosition?: ResponsivePosition
    }
    dialogPosition?: ResponsivePosition
}

interface CustomAnimatedPaperProps extends AnimatedProps {
    onClose?: () => void
    onAnimationLoaded?: () => void
}

const CustomAnimatedPaper: FC<CustomAnimatedPaperProps> = (props) => {
    const { children, animationOrImage, closeIcon, onClose, onAnimationLoaded, dialogPosition, ...other } = props
    const [dotLottie, setDotLottie] = useState<DotLottie>()

    // useEffect(() => {
    //     // This function will be called when the animation starts playing.
    //     function onPlay() {
    //         onAnimationLoaded && onAnimationLoaded()
    //     }

    //     // Listen to events emitted by the DotLottie instance when it is available.
    //     if (dotLottie) {
    //         dotLottie.addEventListener('play', onPlay)
    //         dotLottie.addEventListener('loadError', onPlay)
    //     }

    //     return () => {
    //         // Remove event listeners when the component is unmounted.
    //         if (dotLottie) {
    //             dotLottie.removeEventListener('play', onPlay)
    //             dotLottie.removeEventListener('loadError', onPlay)
    //         }
    //     }
    // }, [dotLottie])

    const dotLottieRefCallback = (dotLottie) => {
        setDotLottie(dotLottie)
    }

    return (
        <Stack
            sx={{
                position: 'relative',
                mt: dialogPosition || 0,
            }}
        >
            {/* Animation or Image Block */}
            {animationOrImage && (
                <Stack
                    sx={{
                        alignItems: 'center',
                        zIndex: 1,
                        position: 'absolute',
                        top: animationOrImage.verticalPosition || { xs: '-35%', md: '-48%' },
                        right: animationOrImage.horizontalPosition || { xs: 0, md: '15%' },
                        left: animationOrImage.horizontalPosition || { xs: 0, md: '15%' },
                    }}
                >
                    {animationOrImage?.src.includes('.lottie') ? (
                        <DotLottieReact
                            src={animationOrImage.src}
                            loop={animationOrImage.loop !== undefined ? animationOrImage.loop : true}
                            autoplay={animationOrImage.autoplay || true}
                            segment={animationOrImage.segment}
                            dotLottieRefCallback={dotLottieRefCallback}
                            renderConfig={{
                                autoResize: true,
                            }}
                        />
                    ) : (
                        <img width="100%" src={animationOrImage.src} />
                    )}
                </Stack>
            )}

            {/* Close icon */}
            {closeIcon?.hide ? null : (
                <IconButton
                    id="close-button"
                    sx={{
                        position: 'absolute',
                        right: closeIcon?.horizontalPosition || { xs: '10px', md: '20px' },
                        top: closeIcon?.verticalPosition || '-45px',
                        zIndex: 1001,
                    }}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
            )}

            {/* Render the rest of the dialog content */}
            <Paper {...other}>{children}</Paper>
        </Stack>
    )
}

interface AnimatedDialogProps {
    name: Dialogs
    children?: ReactNode
    onClose?: () => void
    borderColor?: string
    gap?: string | number
    paperProps?: AnimatedProps
    paddingLess?: boolean
    topPadding?: ResponsivePosition
    triggerImmediately?: boolean
}

const AnimatedDialog: FC<AnimatedDialogProps> = (props) => {
    const { name, children, onClose, borderColor, paperProps = {}, paddingLess, gap, topPadding, triggerImmediately } = props
    const [active, setActive] = useState(true)
    const [animationLoaded, setAnimationLoaded] = useState(false)
    const { unregisterDialog } = useDialogs()

    const onCloseHandler = () => {
        setActive(false)
        unregisterDialog(name, onClose)
    }

    const onAnimationLoaded = () => {
        setAnimationLoaded(true)
    }

    useEffect(() => {
        if (!paperProps.animationOrImage?.src.includes('.lottie')) {
            setAnimationLoaded(true)
        }
    }, [])

    return (
        <Dialog
            open={active}
            borderColor={borderColor}
            horizontalCentered
            verticalCentered
            fullScreen={false}
            paddingLess
            autoHeight
            sx={{
                ...(!triggerImmediately && paperProps.animationOrImage?.src.includes('.lottie') && {
                    '@keyframes fadeInDialog': {
                        from: { opacity: 0 },
                        to: { opacity: 1 },
                    },
                    opacity: 0,
                    display: 'none',
                    ':has(canvas)': {
                        display: 'block',
                        animation: 'fadeInDialog 0.8s ease-in-out forwards',
                        animationDelay: '0.3s',
                    },
                }),
            }}
            PaperComponent={CustomAnimatedPaper}
            PaperProps={{
                ...paperProps,
                sx: {
                    overflowX: 'hidden',
                },
                onClose: onCloseHandler,
                onAnimationLoaded: onAnimationLoaded,
            }}
        >
            <DialogContent
                gap={gap}
                sx={{
                    borderStyle: 'solid',
                    borderColor: borderColor,
                    pt: paddingLess ? 0 : topPadding || { xs: '32px', md: '64px' },
                    pb: paddingLess ? 0 : { xs: '16px', md: '32px' },
                    px: paddingLess ? 0 : { xs: '16px', md: '24px' },
                    mx: 2,
                    backgroundColor: 'background.paper',
                    width: '95%',
                }}
            >
                {children}
            </DialogContent>
        </Dialog>
    )
}

export default AnimatedDialog
